import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { Col, Container, Row, ButtonGroup, ToggleButton } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import "../assets/css/chat.css";
import { io } from "socket.io-client";
import { getChatThreads, getChatHistory, updateChatStatus } from "../Redux/Actions/chatData";
import { addMessageToHistory, clearChatHistory } from "../Redux/Reducers/chatSlice";

export default function Chat() {
  const dispatch = useDispatch();
  const threads = useSelector((state) => state.chatAuth.threadsList) || [];
  const messages = useSelector((state) => state.chatAuth.chatHistory) || [];
  const [activeChat, setActiveChat] = useState(null);
  const activeChatRef = useRef(null);
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(0); // 0 = open, 1 = resolved
  const [unreadThreads, setUnreadThreads] = useState({}); // Instant real-time unread state
  const [localLastSeen, setLocalLastSeen] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("localLastSeenThreads")) || {};
    } catch {
      return {};
    }
  });

  // Initialize new threads so old ones don't get dots, but updated ones do
  useEffect(() => {
    if (threads.length > 0) {
      setLocalLastSeen(prev => {
        let changed = false;
        const next = { ...prev };
        threads.forEach(u => {
          const threadId = u.id || u.support_id || u._id;
          if (!next[threadId]) {
            next[threadId] = new Date(u.updatedAt || u.updated_at || u.createdAt).getTime();
            changed = true;
          }
        });
        if (changed) {
          localStorage.setItem("localLastSeenThreads", JSON.stringify(next));
          return next;
        }
        return prev;
      });
    }
  }, [threads]);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Initialize socket
  useEffect(() => {
    const adminJwt = sessionStorage.getItem("adminToken");
    const socketUrl = process.env.REACT_APP_IMG_URL;
    socketRef.current = io(socketUrl, {
      auth: { token: adminJwt }
    });

    const handleIncomingMessage = (newMsg) => {
      if (!newMsg) return;
      // Find the thread this message belongs to
      const msgThreadId = newMsg.support_id || newMsg.thread_id || newMsg.supportId || newMsg.threadId;
      const isFromUser = newMsg.sender_type !== 0; // 0 is admin, others are user

      if (activeChatRef.current && (activeChatRef.current === msgThreadId || activeChatRef.current === newMsg.id)) {
        dispatch(addMessageToHistory(newMsg));
        // Keep active chat marked as seen
        setLocalLastSeen(prev => {
          const updated = { ...prev, [activeChatRef.current]: Date.now() };
          localStorage.setItem("localLastSeenThreads", JSON.stringify(updated));
          return updated;
        });
      } else if (msgThreadId && isFromUser) {
        // Instantly mark as unread in React state (shows green dot immediately!)
        setUnreadThreads(prev => ({
          ...prev,
          [msgThreadId]: (prev[msgThreadId] || 0) + 1
        }));
      }

      fetchThreads();
    };

    socketRef.current.on("support_message", handleIncomingMessage);
    socketRef.current.on("new_message", handleIncomingMessage);

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [BASE_URL]);

  // Auto-join socket rooms for all chat threads so admin receives real-time messages from all users
  useEffect(() => {
    if (socketRef.current && threads && threads.length > 0) {
      threads.forEach((t) => {
        const tId = t.id || t.support_id || t._id;
        if (tId) {
          socketRef.current.emit("join_support", { support_id: tId });
        }
      });
    }
  }, [threads]);

  // Fetch threads when filter or search changes + Background auto-sync polling
  useEffect(() => {
    fetchThreads();

    const delayDebounceFn = setTimeout(() => {
      fetchThreads();
    }, 500);

    // Auto-sync in background every 4 seconds so brand new users appear without page reload
    const syncInterval = setInterval(() => {
      fetchThreads();
    }, 4000);

    return () => {
      clearTimeout(delayDebounceFn);
      clearInterval(syncInterval);
    };
  }, [statusFilter, searchTerm]);

  const fetchThreads = () => {
    dispatch(getChatThreads({ statusFilter, searchTerm }));
  };

  // Scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSelectChat = async (threadId) => {
    setActiveChat(threadId);
    activeChatRef.current = threadId; // Update ref immediately

    // Clear instant real-time unread state
    setUnreadThreads(prev => {
      const copy = { ...prev };
      delete copy[threadId];
      return copy;
    });

    // Mark thread as seen in localStorage
    setLocalLastSeen(prev => {
        const updated = { ...prev, [threadId]: Date.now() };
        localStorage.setItem("localLastSeenThreads", JSON.stringify(updated));
        return updated;
    });

    // Join socket room
    if (socketRef.current) {
      socketRef.current.emit("join_support", { support_id: threadId });
    }

    // Fetch history
    dispatch(getChatHistory({ threadId }));
  };

  // Auto-select the first chat if none is selected
  useEffect(() => {
    if (!activeChat && threads && threads.length > 0) {
      const firstThread = threads[0];
      const firstThreadId = firstThread.id || firstThread.support_id || firstThread._id;
      if (firstThreadId) {
        handleSelectChat(firstThreadId);
      }
    }
  }, [threads]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (message.trim() !== "" && activeChat) {
      // Send via socket
      socketRef.current.emit("send_support", {
        support_id: activeChat,
        message: message,
        sender_type: 0 // 0 for admin
      });

      setMessage("");
    }
  };

  const handleToggleStatus = async (newStatus) => {
    if (!activeChat) return;
    try {
      await dispatch(updateChatStatus({ threadId: activeChat, status: newStatus }));
      fetchThreads();
      if (newStatus !== statusFilter) {
        setActiveChat(null);
        activeChatRef.current = null;
        dispatch(clearChatHistory());
      }
    } catch (err) {
      console.error("Error updating status", err);
    }
  };

  return (
    <Layout>
      <Container fluid className="g-0">
        <Row>
          <Col lg={12}>
            <div className="main-title-box mb-4 d-flex justify-content-between align-items-center">
              <div className="title-head-left">
                <span>Support</span>
                <h4>Support Chat</h4>
              </div>
              <div>
                <ButtonGroup>
                  <ToggleButton
                    id="toggle-open"
                    type="radio"
                    variant={statusFilter === 0 ? "primary" : "outline-primary"}
                    name="radio"
                    value={0}
                    checked={statusFilter === 0}
                    onChange={() => {
                        setStatusFilter(0);
                        setActiveChat(null);
                        dispatch(clearChatHistory());
                    }}
                  >
                    Open
                  </ToggleButton>
                  <ToggleButton
                    id="toggle-resolved"
                    type="radio"
                    variant={statusFilter === 1 ? "primary" : "outline-primary"}
                    name="radio"
                    value={1}
                    checked={statusFilter === 1}
                    onChange={() => {
                        setStatusFilter(1);
                        setActiveChat(null);
                        dispatch(clearChatHistory());
                    }}
                  >
                    Resolved
                  </ToggleButton>
                </ButtonGroup>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="chat-wrapper">
          {/* Sidebar Chat List */}
          <Col lg={4} className="chat-sidebar">
            <div className="chat-search">
              <input 
                type="text" 
                placeholder="Search chats..." 
                className="form-control" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="chat-list">
              {threads.map((u) => {
                const threadId = u.id || u.support_id || u._id;
                const userName = u.user_id?.user_name || u.user_id?.name || u.user_name || u.name || "User";
                const lastMsg = u.last_message?.message || u.message || u.subject || "No message";
                const timeStr = u.updatedAt || u.updated_at;
                const threadTime = new Date(timeStr || u.createdAt).getTime();
                const unreadCount = u.unread || u.unread_count || u.unreadCount || 0;
                
                // Unread if the thread's timestamp is newer than our local last seen time
                // AND the last message was NOT sent by the admin (sender_type === 0 is admin)
                const isLocalUnread = localLastSeen[threadId] 
                  && threadTime > localLastSeen[threadId]
                  && u.last_message?.sender_type !== 0;
                
                return (
                <div
                  key={threadId}
                  className={`chat-list-item ${activeChat === threadId ? "active" : ""}`}
                  onClick={() => handleSelectChat(threadId)}
                >
                  <div className="chat-avatar">
                    <div className="avatar-placeholder">
                      {userName.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div className="chat-info">
                    <div className="chat-header">
                      <h6>{userName}</h6>
                      <span className="chat-time">
                          {timeStr ? new Date(timeStr).toLocaleDateString() : ""}
                      </span>
                    </div>
                    <div className="chat-last-message">
                      <p>{lastMsg}</p>
                      {unreadThreads[threadId] ? (
                        <span className="unread-badge" style={{ minWidth: '18px', height: '18px', padding: '0 5px', borderRadius: '50px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold' }}>
                          {unreadThreads[threadId]}
                        </span>
                      ) : isLocalUnread ? (
                        <span className="unread-badge" style={{ width: '12px', height: '12px', padding: 0, borderRadius: '50%', display: 'inline-block' }}></span>
                      ) : (
                        unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>
                      )}
                    </div>
                  </div>
                </div>
              )})}
              {threads.length === 0 && <div className="text-center mt-4 text-muted">No chats found.</div>}
            </div>
          </Col>

          {/* Main Chat Area */}
          <Col lg={8} className="chat-main">
            {activeChat ? (
              <div className="chat-window">
                <div className="chat-window-header d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <div className="chat-avatar me-3">
                      <div className="avatar-placeholder">
                        {
                          (threads.find((u) => (u.id || u.support_id || u._id) === activeChat)?.user_id?.user_name || "U").charAt(0).toUpperCase()
                        }
                      </div>
                    </div>
                    <div className="chat-header-info">
                      <h6 className="mb-0">
                          {threads.find((u) => (u.id || u.support_id || u._id) === activeChat)?.user_id?.user_name || "User"}
                      </h6>
                      <span className="text-muted small">Online</span>
                    </div>
                  </div>
                  <div>
                    {statusFilter === 0 ? (
                        <button className="btn btn-sm btn-outline-success" onClick={() => handleToggleStatus(1)}>
                            Mark as Resolved
                        </button>
                    ) : (
                        <button className="btn btn-sm btn-outline-warning" onClick={() => handleToggleStatus(0)}>
                            Reopen Chat
                        </button>
                    )}
                  </div>
                </div>

                <div className="chat-messages">
                  {messages.map((msg, index) => (
                    <div key={msg.id || index} className={`chat-message ${msg.sender_type === 0 ? "sent" : "received"}`}>
                      <div className="message-content">
                        <p>{msg.message}</p>
                        <span className="message-time">
                          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ""}
                        </span>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {statusFilter === 0 ? (
                    <div className="chat-input-area">
                    <form onSubmit={handleSendMessage} className="chat-input-form">
                        <input
                        type="text"
                        placeholder="Type a message..."
                        className="form-control"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        />
                        <button type="submit" className="btn btn-primary send-btn" disabled={!message.trim()}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="white" />
                        </svg>
                        </button>
                    </form>
                    </div>
                ) : (
                    <div className="chat-input-area text-center text-muted p-3">
                        This chat is resolved. Reopen to send messages.
                    </div>
                )}
              </div>
            ) : (
              <div className="no-chat-selected">
                <p>Select a chat to start messaging</p>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </Layout>
  );
}
